// The one place the public website's addresses, titles, descriptions, and search targets are
// written down. The React site, the Worker that serves it, the prerenderer, the generated
// sitemap/robots/llms files, and the search-visibility tooling all read this table, so a page
// cannot exist with one title in the app and a different one in the sitemap.
//
// Adding a page means adding a row here and a component in Website/src/ui. Everything else --
// sitemap entry, prerendered HTML file, structured data, keyword coverage -- follows from the row.

export const siteOrigin = "https://useclearguard.com";
export const siteName = "ClearGuard";
export const appStoreURL = "https://apps.apple.com/app/id6787290924";
export const supportEmail = "support@useclearguard.com";
export const socialImagePath = "/og-image.png";

// Files a search engine fetches to confirm you control the domain. The name and the contents are
// chosen by them, so neither can be tidied. These are public tokens, not secrets.
//
// They are served by the Worker at the exact path rather than left to the asset layer, for two
// reasons that both end in a failed verification: an address ending in .html that is not a page in
// the table would be answered with the not-found page, and Cloudflare's default html_handling
// redirects /foo.html to /foo, which a verifier is not obliged to follow.
export const siteVerifications = Object.freeze({
  "/googlecf3aefa1ed74a900.html": "google-site-verification: googlecf3aefa1ed74a900.html\n"
});

// Search phrases ClearGuard is trying to be found for, each owned by exactly one page. The
// ownership matters: two pages chasing the same phrase compete with each other, and the
// website-seo test fails when a phrase has no page or more than one.
export const keywordClusters = Object.freeze([
  Object.freeze({
    id: "iphone-blocking",
    label: "Blocking porn on iPhone",
    page: "/block-porn-on-iphone",
    keywords: Object.freeze([
      "block porn on iphone",
      "how to block porn on iphone",
      "iphone porn blocker",
      "safari porn blocker",
      "screen time porn blocker",
      "block adult websites on iphone",
      "ios content filter"
    ])
  }),
  Object.freeze({
    id: "short-form-feeds",
    label: "Blocking Reels, Shorts, and other feeds",
    page: "/block-reels-and-shorts",
    keywords: Object.freeze([
      "block instagram reels",
      "block youtube shorts",
      "block short form video",
      "block reels on iphone",
      "distraction blocker for iphone",
      "block tiktok on iphone"
    ])
  }),
  Object.freeze({
    id: "privacy",
    label: "Blocking without monitoring",
    page: "/private-porn-blocker",
    keywords: Object.freeze([
      "porn blocker without accountability partner",
      "private porn blocker",
      "porn blocker without screenshots",
      "content blocker that does not track browsing"
    ])
  }),
  Object.freeze({
    id: "urges",
    label: "Help in the moment",
    page: "/urge-help",
    keywords: Object.freeze([
      "quit porn app",
      "urge help app",
      "porn recovery app",
      "panic button porn blocker"
    ])
  }),
  Object.freeze({
    id: "product",
    label: "Product and brand",
    page: "/",
    keywords: Object.freeze([
      "porn blocker",
      "porn blocker for iphone",
      "clearguard",
      "clearguard app"
    ])
  }),
  Object.freeze({
    id: "pricing",
    label: "Cost and plans",
    page: "/pricing",
    keywords: Object.freeze([
      "free porn blocker for iphone",
      "porn blocker price",
      "clearguard pro cost"
    ])
  })
]);

// `updated` is written down rather than taken from the clock so the generated sitemap is stable and
// its drift check means something. Bump the date when the page's content changes -- and you will be
// made to: Tools/generate_website_seo.mjs digests each page's copy and metadata and refuses to run
// while a page has changed under an unchanged date.
export const publicPages = Object.freeze([
  Object.freeze({
    path: "/",
    title: "Block porn and distractions on iPhone",
    description: "Block porn, Reels, Shorts, apps, and any website you add on iPhone and iPad. Your browsing history stays on the device.",
    updated: "2026-08-17",
    inSitemap: true
  }),
  Object.freeze({
    path: "/block-porn-on-iphone",
    title: "How to block porn on iPhone",
    description: "Step by step: block porn on iPhone and iPad with Screen Time, Safari, and ClearGuard, then check that the blocking is still on.",
    updated: "2026-08-17",
    inSitemap: true,
    breadcrumb: "Guides"
  }),
  Object.freeze({
    path: "/block-reels-and-shorts",
    title: "Block Reels and Shorts on iPhone",
    description: "Turn off Instagram Reels, YouTube Shorts, and other short-form feeds on iPhone and iPad, and block the apps that will not separate the feed.",
    updated: "2026-08-17",
    inSitemap: true,
    breadcrumb: "Guides"
  }),
  Object.freeze({
    path: "/private-porn-blocker",
    title: "A blocker that does not watch your screen",
    description: "ClearGuard blocks porn without screenshots, uploaded browsing history, or a partner reading your feed. See what leaves your device and what never does.",
    updated: "2026-08-17",
    inSitemap: true,
    breadcrumb: "Guides"
  }),
  Object.freeze({
    path: "/urge-help",
    title: "Free offline help for a hard moment",
    description: "Urge Help is free, works offline, and needs no account. Pause, breathe, change rooms, or lock protection for an hour when the pull hits.",
    updated: "2026-08-17",
    inSitemap: true,
    breadcrumb: "Guides"
  }),
  Object.freeze({
    path: "/faq",
    title: "Questions and answers",
    description: "What ClearGuard blocks and what it misses, what it stores, what Pro costs, and what happens to your settings if you stop paying.",
    updated: "2026-08-17",
    inSitemap: true
  }),
  Object.freeze({
    path: "/protection",
    title: "How blocking works on iPhone and iPad",
    description: "The layers ClearGuard uses on iPhone and iPad: Screen Time, the Safari content blocker, the browser extension, encrypted DNS, and its own checks.",
    updated: "2026-08-17",
    inSitemap: true
  }),
  Object.freeze({
    path: "/recovery",
    title: "Recovery that stays private",
    description: "Urge Help is free and works offline. The 30-day lesson program follows once independent professional review is recorded.",
    updated: "2026-08-17",
    inSitemap: true
  }),
  Object.freeze({
    path: "/pricing",
    title: "Free, and what Pro costs",
    description: "Blocking, protection checks, and Urge Help are free on one device. ClearGuard Pro is $49.99 a year or $7.99 a month for synced devices, with a free 7-day trial.",
    updated: "2026-08-18",
    inSitemap: true
  }),
  Object.freeze({
    path: "/downloads",
    title: "Download for iPhone and iPad",
    description: "ClearGuard is available now for iPhone and iPad. Mac, browser, Android, and Windows releases appear here only when each one is ready.",
    updated: "2026-08-17",
    inSitemap: true
  }),
  Object.freeze({
    path: "/platform-status",
    title: "Platform status",
    description: "Where ClearGuard is available today and what is still in testing. Only iPhone and iPad are publicly available.",
    updated: "2026-08-16",
    inSitemap: true
  }),
  Object.freeze({
    path: "/security",
    title: "Security",
    description: "How ClearGuard syncs your settings without collecting your browsing activity, and which parts your devices alone can read.",
    updated: "2026-08-16",
    inSitemap: true
  }),
  Object.freeze({
    path: "/support",
    title: "Support",
    description: "Get ClearGuard working again when a device needs attention, and reach a person when the in-app repair step is not enough.",
    updated: "2026-08-16",
    inSitemap: true
  }),
  Object.freeze({
    path: "/privacy",
    title: "Privacy",
    description: "What ClearGuard stores, what always stays on your devices, which providers it uses, and how long anything is kept.",
    updated: "2026-08-13",
    inSitemap: true
  }),
  Object.freeze({
    path: "/terms",
    title: "Terms",
    description: "ClearGuard product terms, subscription terms, acceptable use, and the limits of what any blocker can promise.",
    updated: "2026-08-13",
    inSitemap: true
  }),
  Object.freeze({
    path: "/account-deletion",
    title: "Delete your account",
    description: "Delete your ClearGuard account and the encrypted data synced with it, and what happens to an active subscription.",
    updated: "2026-08-13",
    inSitemap: true
  }),
  // Rendered as a real page but deliberately kept out of the sitemap: a 404 is not a destination.
  Object.freeze({
    path: "/404",
    title: "Page not found",
    description: "That ClearGuard page does not exist. Head back to the home page and try again.",
    updated: "2026-08-16",
    inSitemap: false,
    noIndex: true
  })
]);

// Which component renders each page. Two gates read a page's real copy through this map: the
// keyword check, which asks whether the page that claims a phrase actually uses its words, and the
// content digest that stops a sitemap lastmod date from drifting away from the page it describes.
// A new row in publicPages without an entry here fails both.
export const pageComponents = Object.freeze({
  "/": ["Website/src/ui/App.tsx", "Home"],
  "/protection": ["Website/src/ui/App.tsx", "ProtectionPage"],
  "/recovery": ["Website/src/ui/App.tsx", "RecoveryPage"],
  "/pricing": ["Website/src/ui/App.tsx", "PricingPage"],
  "/downloads": ["Website/src/ui/App.tsx", "DownloadsPage"],
  "/platform-status": ["Website/src/ui/App.tsx", "PlatformStatusPage"],
  "/security": ["Website/src/ui/App.tsx", "SecurityPage"],
  "/support": ["Website/src/ui/App.tsx", "SupportPage"],
  "/privacy": ["Website/src/ui/App.tsx", "PrivacyPage"],
  "/terms": ["Website/src/ui/App.tsx", "TermsPage"],
  "/account-deletion": ["Website/src/ui/App.tsx", "AccountDeletionPage"],
  "/404": ["Website/src/ui/App.tsx", "NotFound"],
  "/block-porn-on-iphone": ["Website/src/ui/Guides.tsx", "BlockOnIPhonePage"],
  "/block-reels-and-shorts": ["Website/src/ui/Guides.tsx", "ReelsAndShortsPage"],
  "/private-porn-blocker": ["Website/src/ui/Guides.tsx", "PrivateBlockerPage"],
  "/urge-help": ["Website/src/ui/Guides.tsx", "UrgeHelpPage"],
  "/faq": ["Website/src/ui/Guides.tsx", "QuestionsPage"]
});

const pagesByPath = new Map(publicPages.map((page) => [page.path, page]));

export const portalMetadata = Object.freeze({
  path: "/app",
  title: "Your account",
  description: "Manage your blocks, devices, plan, and trusted partner.",
  noIndex: true
});

export const fallbackMetadata = Object.freeze({
  path: "/404",
  title: "Page not found",
  description: "That ClearGuard page does not exist. Head back to the home page and try again.",
  noIndex: true
});

/** Collapses `/protection/` and `/protection/index.html` onto the one address that is indexed. */
export function normalizePath(pathname) {
  if (typeof pathname !== "string" || pathname.length === 0) return "/";
  let path = pathname.split("?")[0].split("#")[0];
  if (path.endsWith("/index.html")) path = path.slice(0, -"index.html".length);
  if (path.length > 1 && path.endsWith("/")) path = path.replace(/\/+$/, "");
  return path.length === 0 ? "/" : path;
}

export function isPortalPath(pathname) {
  const path = normalizePath(pathname);
  return path === "/app" || path.startsWith("/app/");
}

export function findPage(pathname) {
  return pagesByPath.get(normalizePath(pathname)) || null;
}

export function pageMetadata(pathname) {
  if (isPortalPath(pathname)) return portalMetadata;
  return findPage(pathname) || fallbackMetadata;
}

export function documentTitle(pathname) {
  return `${pageMetadata(pathname).title} — ${siteName}`;
}

export function canonicalURL(pathname) {
  const path = normalizePath(pathname);
  return path === "/" ? `${siteOrigin}/` : `${siteOrigin}${path}`;
}

export const sitemapPages = publicPages.filter((page) => page.inSitemap);
export const prerenderPages = publicPages;

const softwareApplication = Object.freeze({
  "@type": "SoftwareApplication",
  "@id": `${siteOrigin}/#app`,
  name: "ClearGuard",
  applicationCategory: "SecurityApplication",
  applicationSubCategory: "Content blocker",
  operatingSystem: "iOS 17.0 or later, iPadOS 17.0 or later",
  description: "Blocks porn, Reels, Shorts, selected apps, and websites you add on iPhone and iPad without uploading your browsing history.",
  url: `${siteOrigin}/`,
  downloadUrl: appStoreURL,
  installUrl: appStoreURL,
  isAccessibleForFree: true,
  offers: [
    { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD", category: "free" },
    { "@type": "Offer", name: "ClearGuard Pro, yearly", price: "49.99", priceCurrency: "USD", category: "subscription" },
    { "@type": "Offer", name: "ClearGuard Pro, monthly", price: "7.99", priceCurrency: "USD", category: "subscription" }
  ],
  featureList: [
    "Block known adult websites in Safari and supported browsers",
    "Block Reels, Shorts, and other short-form feeds",
    "Block selected apps and any website you add",
    "Check that protection is still working",
    "Wait at least 24 hours before protection can be turned off",
    "Offline Urge Help with no account"
  ],
  publisher: { "@id": `${siteOrigin}/#publisher` }
});

const publisher = Object.freeze({
  "@type": "Organization",
  "@id": `${siteOrigin}/#publisher`,
  name: "ClearGuard",
  url: `${siteOrigin}/`,
  // A square mark, not the 1200x630 social banner: `logo` is rendered as an icon, and a wide
  // banner in that slot gets letterboxed or rejected.
  logo: `${siteOrigin}/icon-512.png`,
  email: supportEmail
});

// Questions real visitors type into a search box, answered the way the product actually behaves.
// Keep every answer defensible: it is published as structured data and read back by assistants.
export const faqEntries = Object.freeze([
  Object.freeze({
    path: "/faq",
    question: "Does ClearGuard block porn in every browser on iPhone?",
    answer: "ClearGuard blocks known adult websites in Safari and in browsers that support its extension. Apple's Screen Time restriction covers the system web filter. A browser that refuses those controls is blocked as a whole app instead, and ClearGuard tells you when that happens."
  }),
  Object.freeze({
    path: "/faq",
    question: "Can I turn ClearGuard off whenever I want?",
    answer: "No. Turning protection off always waits at least 24 hours. You can also add a Protection PIN for changes that reduce blocking, and Protection Lock holds your settings until a time you choose. A lock can be extended but cannot end early."
  }),
  Object.freeze({
    path: "/faq",
    question: "Does ClearGuard see the websites I visit?",
    answer: "No. ClearGuard does not retain your browsing history, DNS queries, screenshots, private messages, or PINs. Blocking happens on your device. If you use Pro sync, your block list and Recovery data are encrypted on your device before upload and ClearGuard cannot read them."
  }),
  Object.freeze({
    path: "/faq",
    question: "Is ClearGuard free?",
    answer: "Yes. Blocking, protection checks, the turn-off delay, Protection Lock, and offline Urge Help are free on one device, with no account. ClearGuard Pro is $49.99 a year or $7.99 a month and adds synced devices, a Protection Schedule, and one trusted partner."
  }),
  Object.freeze({
    path: "/faq",
    question: "What happens to my blocks if I stop paying for Pro?",
    answer: "Local free protection stays available. Pro features may stop accepting new changes, but ClearGuard does not silently weaken settings already applied on your devices."
  }),
  Object.freeze({
    path: "/faq",
    question: "Can someone bypass ClearGuard?",
    answer: "On a device its owner controls, some protection depends on permissions that can be removed in Settings. No blocker can promise that every page, app, browser, network, or newly created address is blocked. ClearGuard reports what it can check rather than claiming a device is impossible to bypass."
  }),
  Object.freeze({
    path: "/faq",
    question: "Is ClearGuard available on Mac, Android, or Windows?",
    answer: "Not yet. iPhone and iPad are publicly available today. The Mac app, browser extensions, Android app, and Windows app are still in testing. Each one reaches the downloads page when it is ready."
  }),
  Object.freeze({
    path: "/faq",
    question: "Do I need an accountability partner to use ClearGuard?",
    answer: "No. ClearGuard works with no account and no partner. Pro can connect one trusted partner if you want that, and the partner never receives your browsing history or screenshots."
  }),
  Object.freeze({
    path: "/block-porn-on-iphone",
    question: "Does Screen Time block porn on its own?",
    answer: "Apple's adult website restriction in Screen Time blocks a lot, and it is the right first step. It misses sites Apple has not categorized, it does not cover short-form feeds, and it does not tell you when it has been switched off. ClearGuard adds those layers and checks them."
  }),
  Object.freeze({
    path: "/block-porn-on-iphone",
    question: "How do I know the blocking is still on?",
    answer: "Open Protection in ClearGuard and run Check Protection. It runs a harmless live request without opening an explicit website, then shows Protected, Needs Attention, or Paused with the one step that fixes it."
  }),
  Object.freeze({
    path: "/block-porn-on-iphone",
    question: "Can I block a website that ClearGuard misses?",
    answer: "Yes. Add any website in Settings and it is blocked on that device, and on your other connected devices if you use Pro. You can also report a missed block from the blocked-page screen."
  }),
  Object.freeze({
    path: "/block-reels-and-shorts",
    question: "Can ClearGuard hide only Reels inside Instagram?",
    answer: "In Safari and supported browsers, ClearGuard blocks Reels and Shorts links. Inside a native app, iOS cannot isolate one feed, so choosing that app blocks the whole app. ClearGuard tells you which one you are getting."
  }),
  Object.freeze({
    path: "/block-reels-and-shorts",
    question: "Does blocking Shorts also block the rest of YouTube?",
    answer: "In a browser, no. ClearGuard blocks supported Shorts links and leaves the rest of the site alone. If you select the YouTube app through Apple's picker, the whole app is blocked, because iOS does not let a blocker reach inside it."
  }),
  Object.freeze({
    path: "/private-porn-blocker",
    question: "Does ClearGuard take screenshots of my screen?",
    answer: "No. ClearGuard never takes, stores, or uploads screenshots, and it does not send page images, private messages, or browsing history anywhere."
  }),
  Object.freeze({
    path: "/private-porn-blocker",
    question: "What does my trusted partner actually see?",
    answer: "A trusted partner sees that you asked for support, and whether your protection needs attention. Not your browsing history, screenshots, notes, or check-in answers."
  }),
  Object.freeze({
    path: "/urge-help",
    question: "Does Urge Help need an account or payment?",
    answer: "No. Urge Help is free, works offline, and needs no account. It helps you pause, breathe, change rooms, or start Protection Lock for an hour."
  }),
  Object.freeze({
    path: "/urge-help",
    question: "Is ClearGuard therapy or treatment?",
    answer: "No. ClearGuard is a self-accountability tool for adults. It is not medical care, diagnosis, treatment, or emergency support, and it is not a substitute for a qualified professional."
  })
]);

const howToSteps = Object.freeze([
  Object.freeze({
    name: "Install ClearGuard",
    text: "Install ClearGuard from the App Store on the iPhone or iPad you want protected. Blocking is free and needs no account."
  }),
  Object.freeze({
    name: "Turn on both Safari extensions",
    text: "Open Settings, then Apps, then Safari, then Extensions, and turn on both ClearGuard extensions. One blocks known adult websites, the other checks pages as you open them."
  }),
  Object.freeze({
    name: "Allow Screen Time protection",
    text: "Approve Screen Time access when ClearGuard asks. This applies Apple's adult website restriction and lets ClearGuard block apps you select."
  }),
  Object.freeze({
    name: "Protect other browsers",
    text: "Follow the step ClearGuard shows to cover browsers other than Safari. A browser that will not accept the extension is blocked as a whole app instead."
  }),
  Object.freeze({
    name: "Add anything ClearGuard missed",
    text: "In Settings, turn on Reels and Shorts, pick apps to block, and add websites of your own. Every choice applies to this device, and to your other connected devices with Pro."
  }),
  Object.freeze({
    name: "Make turning it off slow",
    text: "Leave the 24-hour turn-off delay on, add a Protection PIN for changes that reduce blocking, and lock ClearGuard so it cannot be deleted while protection is active."
  }),
  Object.freeze({
    name: "Check that it works",
    text: "Open Protection and run Check Protection. ClearGuard runs a harmless live request without opening an explicit website and shows the one step to fix anything that is off."
  })
]);

// Every ListItem except the last has to carry `item`, so a grouping label with no address of its
// own cannot sit in the trail. `breadcrumb: "Guides"` names a section in llms.txt; there is no
// /guides page, and an intermediate entry pointing nowhere invalidates the whole BreadcrumbList.
function breadcrumbFor(page) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "ClearGuard", item: `${siteOrigin}/` },
      { "@type": "ListItem", position: 2, name: page.title, item: canonicalURL(page.path) }
    ]
  };
}

function faqFor(path) {
  const entries = faqEntries.filter((entry) => entry.path === path);
  if (entries.length === 0) return null;
  return {
    "@type": "FAQPage",
    "@id": `${canonicalURL(path)}#faq`,
    mainEntity: entries.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer }
    }))
  };
}

/**
 * The structured data published on a page, as one `@graph` so a page carries exactly one inline
 * script and therefore exactly one Content-Security-Policy hash.
 */
export function structuredData(pathname) {
  const page = findPage(pathname);
  if (!page || page.noIndex) return null;
  const graph = [];
  if (page.path === "/") {
    graph.push(publisher, {
      "@type": "WebSite",
      "@id": `${siteOrigin}/#website`,
      name: siteName,
      url: `${siteOrigin}/`,
      publisher: { "@id": `${siteOrigin}/#publisher` }
    }, softwareApplication);
  } else {
    graph.push({
      "@type": "WebPage",
      "@id": `${canonicalURL(page.path)}#page`,
      name: `${page.title} — ${siteName}`,
      description: page.description,
      url: canonicalURL(page.path),
      isPartOf: { "@id": `${siteOrigin}/#website` },
      publisher: { "@id": `${siteOrigin}/#publisher` }
    }, breadcrumbFor(page));
  }
  if (page.path === "/pricing" || page.path === "/downloads") graph.push(softwareApplication);
  if (page.path === "/block-porn-on-iphone") {
    graph.push({
      "@type": "HowTo",
      "@id": `${canonicalURL(page.path)}#howto`,
      name: "How to block porn on iPhone and iPad",
      description: page.description,
      totalTime: "PT10M",
      supply: [{ "@type": "HowToSupply", name: "An iPhone or iPad running iOS 17 or later" }],
      tool: [{ "@type": "HowToTool", name: "ClearGuard" }],
      step: howToSteps.map((step, index) => ({
        "@type": "HowToStep",
        position: index + 1,
        name: step.name,
        text: step.text,
        url: `${canonicalURL(page.path)}#step-${index + 1}`
      }))
    });
  }
  const faq = faqFor(page.path);
  if (faq) graph.push(faq);
  return { "@context": "https://schema.org", "@graph": graph };
}

export function structuredDataJSON(pathname) {
  const data = structuredData(pathname);
  // `<` is escaped so the payload cannot close its own script element, and so the text hashed for
  // the Content-Security-Policy is byte-for-byte the text that ends up in the document.
  return data ? JSON.stringify(data).replaceAll("<", "\\u003c") : "";
}

export { howToSteps };

function escapeAttribute(value) {
  return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

export const seoHeadStartMarker = "<!-- clearguard:seo -->";
export const seoHeadEndMarker = "<!-- clearguard:/seo -->";

/**
 * Every head tag that differs from page to page, built in one place so the file written at build
 * time and the fallback the Worker rewrites at the edge cannot describe a page differently.
 */
export function seoHeadHTML(pathname) {
  const meta = pageMetadata(pathname);
  const canonical = canonicalURL(pathname);
  const title = `${meta.title} — ${siteName}`;
  const image = `${siteOrigin}${socialImagePath}`;
  const tags = [
    `<title>${escapeAttribute(title)}</title>`,
    `<meta name="description" content="${escapeAttribute(meta.description)}" />`,
    `<meta name="robots" content="${meta.noIndex ? "noindex, follow" : "index, follow, max-image-preview:large"}" />`,
    `<link rel="canonical" href="${escapeAttribute(canonical)}" />`,
    `<meta property="og:site_name" content="${siteName}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:title" content="${escapeAttribute(title)}" />`,
    `<meta property="og:description" content="${escapeAttribute(meta.description)}" />`,
    `<meta property="og:url" content="${escapeAttribute(canonical)}" />`,
    `<meta property="og:image" content="${escapeAttribute(image)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="ClearGuard, a porn and distraction blocker for iPhone and iPad" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttribute(title)}" />`,
    `<meta name="twitter:description" content="${escapeAttribute(meta.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttribute(image)}" />`
  ];
  const graph = structuredDataJSON(pathname);
  if (graph) tags.push(`<script type="application/ld+json">${graph}</script>`);
  return `${seoHeadStartMarker}\n    ${tags.join("\n    ")}\n    ${seoHeadEndMarker}`;
}

/** Swaps the marked block in a built HTML document for the tags belonging to `pathname`. */
export function replaceSEOHead(html, pathname) {
  const start = html.indexOf(seoHeadStartMarker);
  const end = html.indexOf(seoHeadEndMarker);
  if (start === -1 || end === -1 || end < start) return html;
  return html.slice(0, start) + seoHeadHTML(pathname) + html.slice(end + seoHeadEndMarker.length);
}

export function sitemapXML() {
  const entries = sitemapPages
    .map((page) => `  <url><loc>${canonicalURL(page.path)}</loc><lastmod>${page.updated}</lastmod></url>`)
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${entries}
</urlset>
`;
}

// Agents that actually fetch pages. Named explicitly rather than left to the wildcard so that
// allowing them is a decision recorded in the repo, not an accident of the default rule.
const assistantCrawlers = Object.freeze([
  "GPTBot",
  "OAI-SearchBot",
  "ChatGPT-User",
  "ClaudeBot",
  "Claude-SearchBot",
  "Claude-User",
  "PerplexityBot",
  "Perplexity-User",
  "meta-externalagent",
  "Bingbot",
  "DuckAssistBot",
  "cohere-ai",
  "Amazonbot"
]);

// These two are NOT crawlers. They are tokens that control whether content already fetched by
// Googlebot and Applebot may be used to train generative models, so path rules mean nothing to
// them -- listing Disallow lines here would only look like protection. `Allow: /` is the opt-in.
const trainingControls = Object.freeze(["Google-Extended", "Applebot-Extended"]);

export function robotsTXT() {
  // The account portal is deliberately NOT disallowed. It is linked from the header of every page,
  // and a crawler that is forbidden from fetching it can never read the noindex it serves -- which
  // is how a disallowed URL still ends up listed, with no description under it. Letting the crawl
  // through costs one request for an empty shell and makes the noindex actually count.
  // /app/api/ stays blocked: JSON has nowhere to carry a noindex.
  const disallow = ["/go/", "/app/api/", "/v1/", "/v2/", "/v3/"];
  const rules = disallow.map((path) => `Disallow: ${path}`).join("\n");
  const blocks = [`User-agent: *\n${rules}\nAllow: /`];
  for (const agent of assistantCrawlers) blocks.push(`User-agent: ${agent}\n${rules}\nAllow: /`);
  blocks.push(`# Generative-training controls, not crawlers. Path rules do not apply to them.\n${trainingControls.map((agent) => `User-agent: ${agent}`).join("\n")}\nAllow: /`);
  return `# ClearGuard -- generated by Tools/generate_website_seo.mjs. Do not edit by hand.\n\n${blocks.join("\n\n")}\n\nSitemap: ${siteOrigin}/sitemap.xml\n`;
}

/** A plain-text map of the site for assistants that read one file instead of crawling every page. */
export function llmsTXT() {
  const guides = publicPages.filter((page) => page.breadcrumb === "Guides");
  const guidePaths = new Set(guides.map((page) => page.path));
  const product = sitemapPages.filter((page) => !guidePaths.has(page.path) && !["/privacy", "/terms", "/account-deletion", "/support"].includes(page.path));
  const help = sitemapPages.filter((page) => ["/support", "/privacy", "/terms", "/account-deletion"].includes(page.path));
  const line = (page) => `- [${page.title}](${canonicalURL(page.path)}): ${page.description}`;
  return `# ClearGuard

> ClearGuard blocks porn, Reels, Shorts, selected apps, and websites you add on iPhone and iPad. Blocking, protection checks, and offline Urge Help are free and need no account. ClearGuard does not retain browsing history, DNS queries, or screenshots; anything synced with ClearGuard Pro is encrypted on the device before upload and ClearGuard cannot read it.

ClearGuard is a self-accountability tool for adults. It is not therapy, medical treatment, or monitoring software for another person. Only iPhone and iPad are publicly available today.

## Product

${product.map(line).join("\n")}

## Guides

${guides.map(line).join("\n")}

## Help and policies

${help.map(line).join("\n")}
`;
}
